import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { setAskAicaRightPanelMode, setAskAicaSelectedSources, clearMessages } from '../../../../../../store/dashboard/askAica/actions';
import { DEFAULT_SUGGESTED_QUESTIONS, CONVERSATION_LIST_POLL_MS } from '../../../../../../store/dashboard/askAica/constants';
import styles from './AskAicaV2.module.scss';
import DataSourceSelector from './components/DataSourceSelector/DataSourceSelector';
import { chatbotGif } from '../../../../../../assets/hostedassets';
import LeftPanel from './components/LeftPanel/LeftPanel';
import RightPanel from './components/RightPanel/RightPanel';
import MagicUploadIcon from '../../../../../../assets/Svg/MagicuploadIcon';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_ASK_AICA_MODAL_OPENED, AICA_GLOBAL_ASK_AICA_OPENED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { useSearchParams } from 'react-router-dom';
import { globalAskAicaService } from '../../../../../../store/dashboard/askAica/globalAskAicaService';
import { AgentInput, SuggestedQuestion } from '../../../../../../store/dashboard/askAica/actions';
import { useAskAicaFileActions } from './hooks/useAskAicaFileActions';
import ConversationSidebar from './components/ConversationSidebar/ConversationSidebar';
import PortfolioConversationSidebar from './components/ConversationSidebar/PortfolioConversationSidebar';
import DocxDrawer from '../../../../../../common/DocumentViewer/DocxDrawer';
import FileViewerDrawer from '../../../../../../common/DocumentViewer/FileViewerDrawer';
import { SheetEditorDrawer } from '../../../../../../common/SheetEditor';
import { useAskAicaSurface, ASK_AICA_SURFACE, AskAicaSurface } from './context/AskAicaSurfaceContext';
import { LeadsAskAicaSurfaceProvider } from './context/LeadsAskAicaSurfaceProvider';
import { PortfolioAskAicaSurfaceProvider } from './context/PortfolioAskAicaSurfaceProvider';
import { useVisibilityConfig } from '../../../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../../../utils/constants/visibilityKeys';

interface AskAicaV2Props {
    selectedCompany?: any;
    overviewData?: any;
    global?: boolean;
    /** 'leads' (default) = the company-scoped floating bar + full-window modal.
     * 'portfolio' = the org-less surface, mounted full-window with no floating
     * bar / global icon (RECUR-27369). Parent controls open/close via mount/unmount. */
    surface?: AskAicaSurface;
    /** Portfolio only — invoked by the modal's cross button / Escape / backdrop. */
    onClose?: () => void;
}

/**
 * AskAicaV2Shell — the actual UI. ALWAYS reads its data via useAskAicaSurface(),
 * never useAskAica()/useAppSelector(askAicaReducer.*) directly, so it works
 * identically whichever provider (Leads/Portfolio) an ancestor mounted. See the
 * AskAicaV2 wrapper below for provider selection.
 */
const AskAicaV2Shell: React.FC<AskAicaV2Props> = ({
    selectedCompany,
    overviewData,
    global = false,
    surface = ASK_AICA_SURFACE.LEADS,
    onClose,
}) => {
    const isPortfolio = surface === ASK_AICA_SURFACE.PORTFOLIO;
    const userData = useAppSelector((state) => state.user.userData);
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const { selectedSources, rightPanelMode } = useAppSelector((state) => state.dashboard.askAicaReducer.uiState);
    const {
        clearChat: clearChatMemory,
        sendMessage,
        fetchChatHistory,
        fetchLibraryFiles,
        fetchOrgConversations,
        fileContextOrgId,
        activeOrgId,
        openChat,
    } = useAskAicaSurface();
    const { openFile, addToReports, addingToReports, addedToReports, docxViewer, sheetViewer, fileViewer } =
        useAskAicaFileActions(fileContextOrgId);

    // Show AskAica on all tabs unless explicitly disabled
    const dashboardConfig = useAppSelector((state) => state.dashboard.dashboardReducer.config);
    const currentTabConfig = dashboardConfig?.selectedTabId ? dashboardConfig.tabConfigs?.[dashboardConfig.selectedTabId] : null;
    const isAskAicaEnabledForTab = currentTabConfig?.showAskAica !== false;

    // Portfolio surface: fail-closed opt-in, per PNO (RECUR-27376 §C) — a missing/Hidden
    // config key means OFF, unlike the in-lead tab check above which defaults to ON.
    const { isExplicitlyEnabled } = useVisibilityConfig();
    const isPortfolioAskAicaEnabled = isExplicitlyEnabled(VISIBILITY_KEYS.PORTFOLIO_ASK_AICA.ENABLED);

    // Check if any drawer/modal is open (only affects global icon visibility)
    const investorState = useAppSelector((state) => state.investor);
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const aicaGradeDrawerOpen = useAppSelector((state) => state.dashboard.dashboardReducer.aicaGradeData.showAicaGradeDrawer);

    const isAnyDrawerOpen =
        searchParams.get('magicUpload') === 'true' ||
        searchParams.get('needInfo') === 'true' ||
        investorState.dealDrawer.open ||
        investorState.dealApplicationDrawer.open ||
        investorState.showManageTeamDrawer ||
        investorState.customFilterDrawer.open ||
        commentsThreadState.showCommentsThread ||
        aicaGradeDrawerOpen;

    // Ask bar shows on all main pages, global icon hides when drawers are open
    const showAskAica = isPortfolio ? isPortfolioAskAicaEnabled : isAskAicaEnabledForTab;
    const showGlobalIcon = isAskAicaEnabledForTab && !isAnyDrawerOpen;
    const [isExpanded, setIsExpanded] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [showModalContent, setShowModalContent] = useState(false);
    const [showCollapsedContent, setShowCollapsedContent] = useState(true);
    const barRef = useRef<HTMLDivElement>(null);
    const globalIconRef = useRef<HTMLDivElement>(null);
    const timersRef = useRef<number[]>([]);
    const prevBodyOverflowRef = useRef<string | null>(null);
    const [autoSendMessage, setAutoSendMessage] = useState<string | null>(null);
    const autoFlowKeyRef = useRef<string | undefined>(undefined);
    const autoAgentInputRef = useRef<AgentInput | undefined>(undefined);
    const [initialRect, setInitialRect] = useState<DOMRect | null>(null);
    const [openedFromGlobal, setOpenedFromGlobal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const orgId = fileContextOrgId;
    const sendMessageRef = useRef<((message: string, sources?: string[], flowKey?: string, agentInput?: AgentInput) => void) | null>(null);

    // Clear stale chat state (messages, pagination, suggested questions, library) when the
    // active org changes — compared against Redux (not a local ref) so this fires correctly
    // both on a same-instance company switch and when a fresh AskAicaV2 instance mounts after
    // navigating to a different module for a different company (the global askAicaReducer
    // slice survives that unmount/remount, a local ref would not). Leads only — the
    // portfolio surface has no route org id / clearMessages equivalent.
    useEffect(() => {
        if (isPortfolio) return;
        if (orgId && activeOrgId !== orgId) {
            dispatch(clearMessages(orgId));
        }
    }, [isPortfolio, orgId, activeOrgId, dispatch]);

    const suggestedQuestions = DEFAULT_SUGGESTED_QUESTIONS;

    // Bottom-right fallback origin — matches the global icon's (and the portfolio
    // launcher's, RECUR-27369) fixed position: bottom 50px, right 50px, 60px orb.
    // Shared by handleOpenModal's "no ref available" branch and the portfolio
    // mount-time grow animation below, so both read from one place.
    const setBottomRightOriginVars = () => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Use global icon's expected position (bottom: 50px, right: 50px)
        const iconSize = 60; // From CSS
        const bottom = 50;
        const right = 50;

        const globalIconTop = viewportHeight - bottom - iconSize;
        const globalIconLeft = viewportWidth - right - iconSize;

        document.documentElement.style.setProperty('--ask-bar-initial-width', `${iconSize}px`);
        document.documentElement.style.setProperty('--ask-bar-initial-height', `${iconSize}px`);
        document.documentElement.style.setProperty('--ask-bar-initial-top', `${globalIconTop}px`);
        document.documentElement.style.setProperty('--ask-bar-initial-left', `${globalIconLeft}px`);
        document.documentElement.style.setProperty('--ask-bar-center-x', `${globalIconLeft + iconSize / 2}px`);
        document.documentElement.style.setProperty('--ask-bar-center-y', `${globalIconTop}px`);
    };

    const handleOpenModal = (initialMessageOrEvent?: string | React.MouseEvent, flowKey?: string, agentInput?: AgentInput) => {
        if (userData?.userId && orgId) {
            fetchChatHistory();
            fetchLibraryFiles?.(orgId);
            fetchOrgConversations();
        }

        if (typeof initialMessageOrEvent === 'string') {
            setAutoSendMessage(initialMessageOrEvent);
            autoFlowKeyRef.current = flowKey;
            autoAgentInputRef.current = agentInput;
        } else {
            autoFlowKeyRef.current = undefined;
            autoAgentInputRef.current = undefined;
        }

        // track mixpanel event
        Mixpanel.track(AICA_ASK_AICA_MODAL_OPENED, {
            pnoId: userData?.investorOrgId,
            userId: userData?.userId,
            organizationId: orgId,
        });

        // Capture initial dimensions and position with more precision

        if (barRef.current) {
            // Animation from floating bar
            setOpenedFromGlobal(false);
            const rect = barRef.current.getBoundingClientRect();
            setInitialRect(rect);

            // Set CSS variables with exact pixel values (not percentages)
            document.documentElement.style.setProperty('--ask-bar-initial-width', `${rect.width}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-height', `${rect.height}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-top', `${rect.top}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-left', `${rect.left}px`);

            // Also store the transform origin for accurate scaling
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            document.documentElement.style.setProperty('--ask-bar-center-x', `${centerX}px`);
            document.documentElement.style.setProperty('--ask-bar-center-y', `${centerY}px`);
        } else if (global && globalIconRef.current) {
            // Animation from global icon when barRef is not available
            setOpenedFromGlobal(true);
            const globalRect = globalIconRef.current.getBoundingClientRect();
            setInitialRect(globalRect);

            // Set CSS variables based on global icon position
            document.documentElement.style.setProperty('--ask-bar-initial-width', `${globalRect.width}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-height', `${globalRect.height}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-top', `${globalRect.top}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-left', `${globalRect.left}px`);

            // Set transform origin to center of global icon
            const centerX = globalRect.left + globalRect.width / 2;
            const centerY = globalRect.top + globalRect.height / 2;
            document.documentElement.style.setProperty('--ask-bar-center-x', `${centerX}px`);
            document.documentElement.style.setProperty('--ask-bar-center-y', `${centerY}px`);
        } else {
            // Fallback for when neither ref is available (global case)
            setOpenedFromGlobal(true);
            setBottomRightOriginVars();
        }

        // Start animation sequence
        setIsAnimating(true);
        setIsClosing(false); // Reset closing state

        // Immediately switch to fixed positioning with initial dimensions
        requestAnimationFrame(() => {
            setIsExpanded(true);

            // Hide collapsed content and show modal content after a delay
            const t1 = window.setTimeout(() => {
                setShowCollapsedContent(false);
                setShowModalContent(true);
            }, 150); // Reduced from 250ms to 150ms
            timersRef.current.push(t1);

            const t2 = window.setTimeout(() => {
                setIsAnimating(false);
            }, 300); // Reduced from 500ms to 300ms
            timersRef.current.push(t2);
        });
    };

    const handleCloseModal = () => {
        if (isPortfolio) {
            // Reuse leads' shrink-to-corner sequence: unmount the content first, then
            // shrink the empty box back to the bottom-right corner via shrinkToOriginal
            // (the `&:not(.Expanded).Animating` rule), then actually unmount (onClose)
            // after the 300ms animation — never shrink with content still crammed in.
            setShowModalContent(false);
            setIsClosing(true);
            setBottomRightOriginVars(); // re-pin the corner in case the viewport resized since mount
            requestAnimationFrame(() => {
                setIsExpanded(false);
                setIsAnimating(true);
                const t = window.setTimeout(() => {
                    onClose?.();
                }, 300);
                timersRef.current.push(t);
            });
            return;
        }

        // Close any open viewer drawer along with the modal — otherwise it floats over
        // the page after the chat closes, and its blob URL (fileViewer) never gets revoked.
        docxViewer?.onClose();
        sheetViewer?.onClose();
        fileViewer?.onClose();

        setIsAnimating(true);
        setIsClosing(true);
        setShowModalContent(false); // Hide modal content immediately
        setAutoSendMessage(null);
        autoFlowKeyRef.current = undefined;
        autoAgentInputRef.current = undefined;

        // If opened from global icon, capture current global icon position for accurate close animation
        if (openedFromGlobal && globalIconRef.current) {
            const globalRect = globalIconRef.current.getBoundingClientRect();

            // Update CSS variables with current global icon position
            document.documentElement.style.setProperty('--ask-bar-initial-width', `${globalRect.width}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-height', `${globalRect.height}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-top', `${globalRect.top}px`);
            document.documentElement.style.setProperty('--ask-bar-initial-left', `${globalRect.left}px`);

            const centerX = globalRect.left + globalRect.width / 2;
            const centerY = globalRect.top + globalRect.height / 2;
            document.documentElement.style.setProperty('--ask-bar-center-x', `${centerX}px`);
            document.documentElement.style.setProperty('--ask-bar-center-y', `${centerY}px`);
        }

        setShowCollapsedContent(true); // Show collapsed content immediately

        requestAnimationFrame(() => {
            setIsExpanded(false);

            // Clear the CSS variables after animation completes
            const t3 = window.setTimeout(() => {
                setIsAnimating(false);
                setIsClosing(false);
                setInitialRect(null);
                setOpenedFromGlobal(false);
                // Clean up CSS variables
                document.documentElement.style.removeProperty('--ask-bar-initial-width');
                document.documentElement.style.removeProperty('--ask-bar-initial-height');
                document.documentElement.style.removeProperty('--ask-bar-initial-top');
                document.documentElement.style.removeProperty('--ask-bar-initial-left');
                document.documentElement.style.removeProperty('--ask-bar-center-x');
                document.documentElement.style.removeProperty('--ask-bar-center-y');
            }, 300); // Reduced from 500ms to 300ms to match opening animation
            timersRef.current.push(t3);
        });
    };

    // Close modal on Escape key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && (isExpanded || isPortfolio)) {
                handleCloseModal();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded, isPortfolio]);

    // Prevent body scroll while the chat is open (leads: while expanded; portfolio: always, it's a page mount)
    useEffect(() => {
        const shouldLockScroll = isPortfolio || isExpanded;
        if (shouldLockScroll) {
            if (prevBodyOverflowRef.current === null) prevBodyOverflowRef.current = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        } else {
            if (prevBodyOverflowRef.current !== null) {
                document.body.style.overflow = prevBodyOverflowRef.current;
                prevBodyOverflowRef.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded, isPortfolio]);

    // Poll the conversation list while the modal is open — leads only, to keep
    // its ConversationSidebar fresh. Cleared on close/unmount so no timer leaks
    // across company switches or modal close. Portfolio's sidebar shows the same
    // single conversation for the whole session (single-conversation-per-user
    // model, 2026-08-10) — it's (re)hydrated once via openChat's resume-on-open
    // (F-B), and refreshed on demand by its own actions (Delete Chat, a new
    // turn), so it needs no polling.
    useEffect(() => {
        if (isPortfolio || !isExpanded) return;
        const id = window.setInterval(() => {
            fetchOrgConversations();
        }, CONVERSATION_LIST_POLL_MS);
        return () => window.clearInterval(id);
    }, [isExpanded, isPortfolio, fetchOrgConversations]);

    // Auto-send message when modal content is shown and there's a message to send
    useEffect(() => {
        if ((showModalContent || isPortfolio) && autoSendMessage && sendMessageRef.current) {
            const flowKeyValue = autoFlowKeyRef.current;
            const agentInputValue = autoAgentInputRef.current;
            const timer = setTimeout(() => {
                sendMessageRef.current!(autoSendMessage, undefined, flowKeyValue, agentInputValue);
                setAutoSendMessage(null);
                autoFlowKeyRef.current = undefined;
                autoAgentInputRef.current = undefined;
            }, 100);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModalContent, isPortfolio, autoSendMessage]);

    useEffect(() => {
        if (global && !isPortfolio) {
            globalAskAicaService.registerAskAicaInstance(
                handleOpenModal,
                (message: string, sources?: string[], flowKey?: string, agentInput?: AgentInput) => {
                    if (sendMessageRef.current) {
                        sendMessageRef.current(message, sources, flowKey, agentInput);
                    } else {
                        sendMessage(message, flowKey, agentInput);
                    }
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [global, isPortfolio, sendMessage]);

    // Global cleanup on unmount
    useEffect(() => {
        return () => {
            timersRef.current.forEach((id) => clearTimeout(id));
            timersRef.current = [];
            if (prevBodyOverflowRef.current !== null) {
                document.body.style.overflow = prevBodyOverflowRef.current;
                prevBodyOverflowRef.current = null;
            }
            if (global && !isPortfolio) {
                globalAskAicaService.registerAskAicaInstance(null, null);
            }
            [
                '--ask-bar-initial-width',
                '--ask-bar-initial-height',
                '--ask-bar-initial-top',
                '--ask-bar-initial-left',
                '--ask-bar-center-x',
                '--ask-bar-center-y',
            ].forEach((v) => document.documentElement.style.removeProperty(v));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Portfolio opens once, on mount (parent controls mount/unmount as open/close) —
    // resumes the most recent conversation, mirroring the old useGlobalAskAicaModal's
    // openChat-on-open (A-14). Also seeds the bottom-right origin vars the portfolio
    // container's static `.Expanded` class animates from (RECUR-27369 item B) — a
    // layout effect (not a regular effect) so the vars are set before the browser's
    // first paint, matching leads' own pre-paint var-then-class-flip sequencing.
    useLayoutEffect(() => {
        if (!isPortfolio) return;
        openChat?.();
        // Distinct event from the leads AICA_ASK_AICA_MODAL_OPENED (F-5) — keeps
        // portfolio and leads analytics separable.
        Mixpanel.track(AICA_GLOBAL_ASK_AICA_OPENED, {
            pnoId: userData?.investorOrgId,
            userId: userData?.userId,
        });
        // Mirror leads' global-icon open sequence exactly, reusing the same state
        // vars + keyframes: empty box grows from the bottom-right corner, then the
        // 3-panel content mounts at 150ms once the box has grown (never crammed
        // into the orb), matching handleOpenModal's timing.
        setOpenedFromGlobal(true);
        setBottomRightOriginVars();
        setIsAnimating(true);
        setIsClosing(false);
        requestAnimationFrame(() => {
            setIsExpanded(true);
            const t1 = window.setTimeout(() => {
                setShowModalContent(true);
            }, 150);
            timersRef.current.push(t1);
            const t2 = window.setTimeout(() => {
                setIsAnimating(false);
            }, 300);
            timersRef.current.push(t2);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPortfolio]);

    // Leads-only floating-bar affordance (the collapsed bar never renders for
    // portfolio) — unchanged from pre-refactor behaviour.
    const handleDataSourceChange = (sources: string[]) => {
        dispatch(setAskAicaSelectedSources(sources));
    };

    const handleQuestionClick = (question: string, flowKey?: string) => {
        autoFlowKeyRef.current = flowKey;
        handleOpenModal();
        setAutoSendMessage(question);
    };

    const handleSuggestionClick = (suggestion: SuggestedQuestion) => {
        const { text, flowKey } = suggestion;
        if (!isExpanded) {
            autoFlowKeyRef.current = flowKey;
            handleOpenModal();
            setAutoSendMessage(text);
        } else {
            if (sendMessageRef.current) {
                sendMessageRef.current(text, undefined, flowKey);
            } else {
                sendMessage(text, flowKey);
            }
        }
    };

    // Handle backdrop click to close modal
    const handleBackdropClick = (event: React.MouseEvent) => {
        if (isExpanded && event.target === event.currentTarget) {
            handleCloseModal();
        }
    };

    if (!showAskAica) return <></>;

    const fileViewerDrawers = (
        <>
            {docxViewer && (
                <DocxDrawer
                    open
                    onClose={docxViewer.onClose}
                    fileName={docxViewer.fileName}
                    blob={docxViewer.blob}
                    onDownload={docxViewer.onDownload}
                    onAddToReports={docxViewer.onAddToReports}
                    addingToReports={addingToReports}
                    addedToReports={addedToReports}
                />
            )}
            {sheetViewer && (
                <SheetEditorDrawer
                    open
                    onClose={sheetViewer.onClose}
                    fileName={sheetViewer.fileName}
                    sheets={sheetViewer.sheets}
                    onAddToReports={sheetViewer.onAddToReports}
                    addingToReports={addingToReports}
                    addedToReports={addedToReports}
                />
            )}
            {fileViewer && (
                <FileViewerDrawer
                    heading={fileViewer.fileName}
                    fileUrl={fileViewer.url}
                    mimeType={fileViewer.mimeType}
                    onDownload={fileViewer.onDownload}
                    onClose={fileViewer.onClose}
                />
            )}
        </>
    );

    // Portfolio: always-open full-window layout, no floating bar / global icon —
    // the entry affordance + open/close boolean live in Portfolio.tsx (RECUR-27369).
    // Sidebar restored 2026-08-10 (trimmed, leads-style, visual parity): "Your
    // Conversations" only — under the single-conversation-per-user model this is
    // always 0 or 1 row, so there's nothing to switch between and no "Team's
    // Conversations"/"New Chat" affordances. See PortfolioConversationSidebar.tsx.
    if (isPortfolio) {
        return (
            <>
                {/* Same growToFullscreen/shrinkToOriginal state machine as leads
                    (isExpanded/isAnimating), driven by the portfolio open effect and
                    the isPortfolio branch of handleCloseModal above. The box grows
                    empty from the bottom-right corner; the 3-panel content mounts only
                    once showModalContent flips at 150ms — identical timing to leads. */}
                <div
                    className={`${styles.FloatingAskBar} ${isExpanded ? styles.Expanded : ''} ${isAnimating ? styles.Animating : ''}`}
                    data-testid="ask-aica-portfolio-modal"
                >
                    {showModalContent && (
                        <div className={`${styles.ModalContent} ${styles.visible}`}>
                            <PortfolioConversationSidebar />
                            <LeftPanel
                                selectedCompany={selectedCompany}
                                overviewData={overviewData}
                                onSendMessageRef={sendMessageRef}
                                onClearChat={clearChatMemory}
                                onOpenFile={openFile}
                                onAddFileToReports={addToReports}
                            />
                            <RightPanel
                                mode="reasoning"
                                onModeChange={() => {}}
                                onCrossButtonClick={handleCloseModal}
                                onSuggestionClick={handleSuggestionClick}
                                onOpenFile={openFile}
                                onAddFileToReports={addToReports}
                                surface={ASK_AICA_SURFACE.PORTFOLIO}
                            />
                        </div>
                    )}
                </div>
                {fileViewerDrawers}
            </>
        );
    }

    return (
        <>
            {/* Backdrop - only show when expanded and hide during closing when opened from global */}
            {isExpanded && !(openedFromGlobal && isClosing) && (
                <div className={`${styles.Backdrop} ${isAnimating ? styles.BackdropAnimating : ''}`} onClick={handleBackdropClick} />
            )}

            {/* Expandable Ask Bar */}
            {(!global || showModalContent || (openedFromGlobal && isAnimating)) && (
                <div
                    className={`${styles.FloatingAskBar} ${isExpanded ? styles.Expanded : ''} ${isAnimating ? styles.Animating : ''}`}
                    ref={barRef}
                >
                    {/* Collapsed Content - hidden during closing animation when opened from global */}
                    {showCollapsedContent && !(openedFromGlobal && isClosing) && (
                        <div className={styles.CollapsedContent}>
                            {/* Row 1: Ask anything text */}
                            <div className={styles.Row1} onClick={handleOpenModal}>
                                <span className={styles.AskAnythingText}>Ask anything</span>
                            </div>

                            {/* Row 2: Data source selector + Suggested questions */}
                            <div className={styles.Row2}>
                                {/* Data Source Selector */}
                                <div className={styles.DataSourceSection}>
                                    <DataSourceSelector
                                        selectedSources={selectedSources}
                                        onSelectionChange={handleDataSourceChange}
                                        onClose={() => {}}
                                        buttonStyle="collapsed"
                                    />
                                </div>

                                {/* Suggested Questions - 70% */}
                                <div className={styles.SuggestedQuestionsSection} onClick={handleOpenModal}>
                                    {suggestedQuestions.map((question) => (
                                        <div
                                            key={question.id}
                                            className={styles.QuestionPill}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleQuestionClick(question.text, question.flowKey);
                                            }}
                                        >
                                            <MagicUploadIcon colour="#4D4D4D" />
                                            {question.display || question.text}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Modal Content - shown when expanded */}
                    {showModalContent && (
                        <div className={`${styles.ModalContent} ${showModalContent ? styles.visible : ''}`}>
                            {/* Conversation Sidebar */}
                            <ConversationSidebar fetchChatHistory={fetchChatHistory} />

                            {/* Left Panel - Chat Interface */}
                            <LeftPanel
                                selectedCompany={selectedCompany}
                                overviewData={overviewData}
                                onSendMessageRef={sendMessageRef}
                                onClearChat={clearChatMemory}
                                onOpenFile={openFile}
                                onAddFileToReports={addToReports}
                            />

                            {/* Right Panel - Suggestions/Reasoning */}
                            <RightPanel
                                mode={rightPanelMode}
                                onModeChange={(mode) => dispatch(setAskAicaRightPanelMode(mode))}
                                onCrossButtonClick={handleCloseModal}
                                onSuggestionClick={handleSuggestionClick}
                                onOpenFile={openFile}
                                onAddFileToReports={addToReports}
                            />
                        </div>
                    )}
                </div>
            )}

            {global && showGlobalIcon && (
                <div className={styles.GlobalIcon} ref={globalIconRef} onClick={handleOpenModal}>
                    <img src={chatbotGif} height={'60px'} />
                </div>
            )}

            {/* File viewer drawers — mounted once, driven by useAskAicaFileActions.
                Docx/Sheet feed on the pre-fetched Athena blob; never the raw fileUrl. */}
            {fileViewerDrawers}
        </>
    );
};

/**
 * AskAicaV2 — selects and mounts the surface provider (Leads/Portfolio) around
 * AskAicaV2Shell (RECUR-27369 Option B). Every existing leads call site
 * (`<AskAicaV2 global ... />` in Dashboard/Deals/DataRoom/Reports/DebtProducts)
 * is unchanged — the provider selection lives here, not at each mount site.
 */
const AskAicaV2: React.FC<AskAicaV2Props> = (props) => {
    const surface = props.surface ?? ASK_AICA_SURFACE.LEADS;
    if (surface === ASK_AICA_SURFACE.PORTFOLIO) {
        return (
            <PortfolioAskAicaSurfaceProvider>
                <AskAicaV2Shell {...props} surface={ASK_AICA_SURFACE.PORTFOLIO} />
            </PortfolioAskAicaSurfaceProvider>
        );
    }
    return (
        <LeadsAskAicaSurfaceProvider>
            <AskAicaV2Shell {...props} surface={ASK_AICA_SURFACE.LEADS} />
        </LeadsAskAicaSurfaceProvider>
    );
};

export default AskAicaV2;
